/**
 * Listado de remitos ANMAT pendientes de confirmar (recepción).
 *
 * Datos REALES desde el backend, que pega a riga-traza-v2 → ANMAT
 * (`GET /api/in/transacciones-no-confirmadas-agrupadas?max_remitos=N`).
 *
 * 1 card por remito. En la card van los datos del remito (no las series).
 *
 * Por ahora es solo listado — el flujo "Confirmar" llega después.
 */

import { useState } from 'react';
import { Loader2, Package, Search, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useRecepcionPendientes } from '@/api/queries';
import { useSessionStore } from '@/stores/session';
import type { RecepcionRemito } from '@/api/contracts/recepcion-pendientes';

const MAX_REMITOS_LIMITE = 500;

/**
 * Cantidad de remitos que se le pide a ANMAT (max_remitos). Configurable por env
 * (VITE_RECEPCION_MAX_REMITOS_DEFAULT); si no está o es inválida, fallback a 3.
 * Valor FIJO — ya no se edita desde la UI.
 */
function parseMaxRemitosDefault(): number {
  const raw = Number(import.meta.env.VITE_RECEPCION_MAX_REMITOS_DEFAULT);
  if (!Number.isInteger(raw) || raw < 1 || raw > MAX_REMITOS_LIMITE) return 3;
  return raw;
}
const MAX_REMITOS_DEFAULT = parseMaxRemitosDefault();

type Campo = 'remito' | 'factura';

interface PendientesRecepcionListProps {
  onAbrirRemito?: (remito: RecepcionRemito) => void;
}

export function PendientesRecepcionList({
  onAbrirRemito,
}: PendientesRecepcionListProps = {}) {
  const ubicacionActivaId = useSessionStore((s) => s.ubicacionActivaId);

  // Estado del buscador: `campo`/`texto` es lo que el usuario tipea; `aplicado`
  // es lo que dispara la query (se setea recién al buscar, no en cada tecla).
  const [campo, setCampo] = useState<Campo>('remito');
  const [texto, setTexto] = useState('');
  const [aplicado, setAplicado] = useState<{ remito?: string; factura?: string }>({});
  // Sin autobúsqueda al entrar: la query recién se dispara cuando el usuario
  // busca o toca "Mostrar pendientes" (evita pegarle a ANMAT al montar).
  const [habilitado, setHabilitado] = useState(false);

  const { data, isLoading, error, isFetching } = useRecepcionPendientes(
    ubicacionActivaId,
    MAX_REMITOS_DEFAULT,
    aplicado,
    habilitado,
  );

  const remitos = data?.remitos ?? [];
  const hayBusqueda = Boolean(aplicado.remito || aplicado.factura);

  const buscar = (e: React.FormEvent) => {
    e.preventDefault();
    const valor = texto.trim();
    // Solo búsqueda PUNTUAL: sin texto no se dispara nada (no mostramos todos).
    if (!valor) return;
    setAplicado({ [campo]: valor });
    setHabilitado(true);
  };

  const limpiar = () => {
    setTexto('');
    setAplicado({});
    setHabilitado(false); // volver al prompt (no mostrar todos los pendientes)
  };

  return (
    <div className="space-y-4">
      <BuscadorRemito
        campo={campo}
        texto={texto}
        hayBusqueda={hayBusqueda}
        isFetching={isFetching}
        onCampo={setCampo}
        onTexto={setTexto}
        onBuscar={buscar}
        onLimpiar={limpiar}
      />

      {!habilitado ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center gap-3 py-12 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
              <Package className="h-6 w-6 text-gray-500" />
            </div>
            <div>
              <h3 className="font-semibold text-everex-azul">Buscá una recepción pendiente</h3>
              <p className="mt-1 text-xs text-gray-600">
                Ingresá un nº de remito o factura y tocá <b>Buscar</b>.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : error ? (
        <Alert variant="destructive">
          <AlertDescription>
            No se pudo traer los pendientes:{' '}
            {error instanceof Error ? error.message : String(error)}
          </AlertDescription>
        </Alert>
      ) : isLoading && !data ? (
        <div className="flex items-center justify-center py-12 text-gray-600">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          <span>Consultando ANMAT...</span>
        </div>
      ) : (
        <RemitosResultado
          remitos={remitos}
          total={data?.totalRemitos}
          hayBusqueda={hayBusqueda}
          isFetching={isFetching}
          onAbrirRemito={onAbrirRemito}
        />
      )}
    </div>
  );
}

function RemitosResultado({
  remitos,
  total,
  hayBusqueda,
  isFetching,
  onAbrirRemito,
}: {
  remitos: RecepcionRemito[];
  total?: number;
  hayBusqueda: boolean;
  isFetching: boolean;
  onAbrirRemito?: (remito: RecepcionRemito) => void;
}) {
  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-600">
        {remitos.length} remito{remitos.length === 1 ? '' : 's'} pendiente
        {remitos.length === 1 ? '' : 's'}
        {!hayBusqueda && total !== undefined ? ` · ${total} en total` : ''}
      </p>

      {remitos.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <Package className="h-6 w-6 text-green-600" />
            </div>
            <h3 className="font-semibold text-everex-azul">
              {hayBusqueda ? 'Sin resultados' : 'Todo al día'}
            </h3>
            <p className="mt-1 text-xs text-gray-600">
              {hayBusqueda
                ? 'Ningún remito pendiente coincide con la búsqueda.'
                : 'No hay ingresos pendientes para este GLN.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div
          className={cn(
            'grid grid-cols-1 gap-3 md:grid-cols-2',
            isFetching && 'opacity-60 transition-opacity',
          )}
        >
          {remitos.map((remito, i) => (
            <RemitoCard
              key={`${remito.numeroRemito ?? '?'}-${i}`}
              remito={remito}
              staggerIndex={i}
              onClick={onAbrirRemito ? () => onAbrirRemito(remito) : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function RemitoCard({
  remito,
  staggerIndex,
  onClick,
}: {
  remito: RecepcionRemito;
  staggerIndex?: number;
  onClick?: () => void;
}) {
  const dataStagger = staggerIndex !== undefined ? Math.min(staggerIndex, 11) : undefined;
  const clickable = Boolean(onClick);
  const conError =
    remito.localTask?.estado === 'error' || remito.localTask?.estado === 'pendiente';

  // Tomamos el evento de la primera serie como evento representativo del remito.
  const evento = remito.series[0]?.descripcionEvento ?? null;

  return (
    <Card
      onClick={onClick}
      data-stagger={dataStagger}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      className={cn(
        dataStagger !== undefined && 'stagger-item',
        clickable && 'cursor-pointer transition hover:shadow-md',
        // Remitos con un intento de traza previo en error → toda la card en
        // rojo claro para que destaque en el listado. Mismo patrón que el
        // listado de pendientes de despacho.
        conError
          ? 'border-red-300 bg-red-50 hover:border-red-500'
          : clickable && 'hover:border-everex-naranja',
      )}
    >
      <CardContent className="pt-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <span className="badge badge-purple">
                <b>RI</b> · Recepción ingreso
              </span>
              {remito.numeroRemito ? (
                <span className="text-sm font-bold text-everex-azul">
                  {remito.numeroRemito}
                </span>
              ) : null}
            </div>

            {evento && <div className="mt-1 text-xs font-medium text-gray-700">{evento}</div>}

            <div className="mt-1 text-xs text-gray-600">
              Origen: <b>{remito.razonSocialOrigen ?? '—'}</b>
            </div>

            <div className="mt-1 text-xs text-gray-600">
              Factura: <span className="font-mono text-gray-800">{remito.numeroFactura ?? '—'}</span>
            </div>
          </div>

          <div className="text-right">
            <BadgeRecepcion remito={remito} />
            <div className="mt-1 text-xs text-gray-600">
              {remito.cantSeries} serie{remito.cantSeries === 1 ? '' : 's'}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Badge del estado del remito. Default amarillo "Por confirmar". Si hay un
 * `localTask` con estado='error' o 'pendiente' (intento previo que no cerró
 * bien), pinta rojo igual que el "Error de traza" del listado de despacho.
 */
function BadgeRecepcion({ remito }: { remito: RecepcionRemito }) {
  const local = remito.localTask;
  if (local && (local.estado === 'error' || local.estado === 'pendiente')) {
    const conError = local.totales.error;
    return (
      <span
        className="badge badge-red"
        title={
          conError > 0
            ? `${conError} de ${local.totales.total} series con error en el último intento`
            : 'Intento previo sin completar'
        }
      >
        Error de traza
      </span>
    );
  }
  return <span className="badge badge-yellow">Por confirmar</span>;
}

function BuscadorRemito({
  campo,
  texto,
  hayBusqueda,
  isFetching,
  onCampo,
  onTexto,
  onBuscar,
  onLimpiar,
}: {
  campo: Campo;
  texto: string;
  hayBusqueda: boolean;
  isFetching: boolean;
  onCampo: (c: Campo) => void;
  onTexto: (t: string) => void;
  onBuscar: (e: React.FormEvent) => void;
  onLimpiar: () => void;
}) {
  return (
    <Card>
      <CardContent className="pt-4">
        <form onSubmit={onBuscar} className="flex flex-wrap items-center gap-2">
          {/* Toggle remito / factura */}
          <div className="inline-flex overflow-hidden rounded-md border border-gray-200">
            {(['remito', 'factura'] as const).map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => onCampo(c)}
                className={cn(
                  'px-3 py-1.5 text-xs capitalize transition',
                  campo === c
                    ? 'bg-everex-azul font-semibold text-white'
                    : 'bg-white text-gray-600 hover:text-everex-azul',
                )}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="relative min-w-[200px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              value={texto}
              onChange={(e) => onTexto(e.target.value)}
              placeholder={`Buscar por n° de ${campo}`}
              className="pl-9 font-mono"
            />
          </div>

          <Button
            type="submit"
            disabled={isFetching}
            className="bg-everex-naranja text-white hover:bg-everex-naranja-hover"
          >
            {isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            <span className="ml-1.5">Buscar</span>
          </Button>

          {hayBusqueda && (
            <Button type="button" variant="outline" onClick={onLimpiar} title="Limpiar búsqueda">
              <X className="h-4 w-4" />
            </Button>
          )}
        </form>
      </CardContent>
    </Card>
  );
}
